import React from 'react'
import { Button, Input, Typography } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import { IMAGES_URL } from 'shared/constants/urls'
import { IAiEnhancementApplyData, IAiEnhancementDraft, IAiProductPhoto } from '../types'
import styles from '../AiEnhancementModal.module.scss'

interface IEnhancementReviewProps {
  initialDescription: string
  selectedPhoto: IAiProductPhoto | null
  draft: IAiEnhancementDraft
  onAccept: (data: IAiEnhancementApplyData) => void | Promise<void>
  onDone: () => void
}

type TReviewDecision = 'accepted' | 'rejected'

const getImageSrc = (image?: string | null) => {
  if (!image) {
    return ''
  }

  if (/^https?:\/\//i.test(image)) {
    return image
  }

  return `${IMAGES_URL}${image}`
}

export const EnhancementReview: React.FC<IEnhancementReviewProps> = ({
  initialDescription,
  selectedPhoto,
  draft,
  onAccept,
  onDone
}) => {
  const [description, setDescription] = React.useState(
    draft.description?.new_desc ?? ''
  )
  const [descriptionDecision, setDescriptionDecision] =
    React.useState<TReviewDecision | null>(null)
  const [imageDecision, setImageDecision] =
    React.useState<TReviewDecision | null>(null)
  const [acceptingDescription, setAcceptingDescription] =
    React.useState(false)
  const [acceptingImage, setAcceptingImage] = React.useState(false)

  const hasDescription = Boolean(draft.description)
  const hasImage = Boolean(draft.image)
  const resultImageSrc = getImageSrc(draft.image?.image)

  React.useEffect(() => {
    setDescription(draft.description?.new_desc ?? '')
    setDescriptionDecision(null)
  }, [draft.description?.new_desc])

  React.useEffect(() => {
    setImageDecision(null)
  }, [draft.image?.image])

  const finishIfComplete = (
    nextDescriptionDecision: TReviewDecision | null,
    nextImageDecision: TReviewDecision | null
  ) => {
    const isComplete =
      (!hasDescription || Boolean(nextDescriptionDecision)) &&
      (!hasImage || Boolean(nextImageDecision))

    if (isComplete) {
      onDone()
    }
  }

  const handleAcceptDescription = async () => {
    setAcceptingDescription(true)
    try {
      await onAccept({ description })
      setDescriptionDecision('accepted')
      finishIfComplete('accepted', imageDecision)
    } catch {
    } finally {
      setAcceptingDescription(false)
    }
  }

  const handleRejectDescription = () => {
    setDescriptionDecision('rejected')
    finishIfComplete('rejected', imageDecision)
  }

  const handleAcceptImage = async () => {
    if (!draft.image?.image) {
      return
    }

    setAcceptingImage(true)
    try {
      await onAccept({
        image: draft.image.image,
        replacedImageUid: selectedPhoto?.uid
      })
      setImageDecision('accepted')
      finishIfComplete(descriptionDecision, 'accepted')
    } catch {
    } finally {
      setAcceptingImage(false)
    }
  }

  const handleRejectImage = () => {
    setImageDecision('rejected')
    finishIfComplete(descriptionDecision, 'rejected')
  }

  const getDecisionText = (decision: TReviewDecision) => {
    return decision === 'accepted' ? 'Принято' : 'Отклонено'
  }

  return (
    <div className={styles.reviewWrapper}>
      {draft.description && (
        <div className={styles.diffSection}>
          <Typography.Title level={4}>Описание</Typography.Title>
          <div className={styles.diffGrid}>
            <div className={styles.diffColumn}>
              <Typography.Text strong>Было</Typography.Text>
              <Input.TextArea
                value={initialDescription}
                readOnly
                autoSize={{ minRows: 8, maxRows: 16 }}
              />
            </div>
            <div className={styles.diffColumn}>
              <Typography.Text strong>Стало</Typography.Text>
              <Input.TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                autoSize={{ minRows: 8, maxRows: 16 }}
              />
            </div>
          </div>
          {descriptionDecision ? (
            <Typography.Text className={styles.reviewDecision}>
              {getDecisionText(descriptionDecision)}
            </Typography.Text>
          ) : (
            <div className={styles.sectionReviewActions}>
              <Button onClick={handleRejectDescription}>
                Отклонить описание
              </Button>
              <Button
                type='primary'
                loading={acceptingDescription}
                onClick={handleAcceptDescription}
              >
                Принять описание
              </Button>
            </div>
          )}
        </div>
      )}

      {draft.image && (
        <div className={styles.diffSection}>
          <Typography.Title level={4}>Картинка товара</Typography.Title>
          <div className={styles.diffGrid}>
            <div className={styles.diffColumn}>
              <Typography.Text strong>Было</Typography.Text>
              <div className={styles.reviewImage}>
                {selectedPhoto?.thumbUrl && (
                  <img src={selectedPhoto.thumbUrl} alt='Текущее фото товара' />
                )}
              </div>
            </div>
            <div className={styles.diffColumn}>
              <Typography.Text strong>Стало</Typography.Text>
              <div className={styles.reviewImage}>
                <img src={getImageSrc(draft.image.image)} alt='AI фото товара' />
              </div>
            </div>
          </div>
          {imageDecision ? (
            <Typography.Text className={styles.reviewDecision}>
              {getDecisionText(imageDecision)}
            </Typography.Text>
          ) : (
            <div className={styles.sectionReviewActions}>
              <Button onClick={handleRejectImage}>
                Отклонить картинку
              </Button>
              <Button
                type='primary'
                loading={acceptingImage}
                onClick={handleAcceptImage}
              >
                Принять картинку
              </Button>
              <a
                href={resultImageSrc}
                download
                target='_blank'
                rel='noreferrer'
              >
                <Button icon={<DownloadOutlined />}>
                  Скачать
                </Button>
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
